import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { requireOrganizationId } from '../utils/request';

export type WarehouseQueryKey = readonly [
  string,
  string | undefined,
  ...unknown[],
];

/**
 * Loads one organization's data. Send the request with
 * `warehouseRequestOptions(organizationId, signal)`, so it names the key's
 * organization explicitly and a superseded fetch is cancelled (S3).
 */
export type WarehouseFetcher<T> = (
  organizationId: string,
  signal?: AbortSignal,
) => Promise<T>;

export interface WarehouseQueryOptions {
  enabled?: boolean;
  keepPreviousData?: boolean;
  retry?: boolean;
}

/** Master key shape (review S3): prefix, organizationId, then the rest. */
export function warehouseQueryKey(
  scope: string,
  organizationId: string | undefined,
  ...parts: unknown[]
): WarehouseQueryKey {
  return [scope, organizationId, ...parts];
}

/**
 * The only way the Magazyn section reads the API. The QueryClient is not
 * cleared when the user switches organization, so the organization lives in
 * the key, the fetcher gets that same organization for its request, a fetch
 * the key no longer needs is cancelled through `signal`, and keepPreviousData
 * never crosses an organization boundary.
 */
export function useWarehouseQuery<T>(
  scope: string,
  parts: readonly unknown[],
  fetcher: WarehouseFetcher<T>,
  options: WarehouseQueryOptions = {},
): UseQueryResult<T> {
  const { selectedOrganization } = useOrganizationContext();
  const organizationId = selectedOrganization?.id;
  // The organization of the last result this hook got for its own key (a
  // fetch or a cache hit, never carried-over data). keepPreviousData holds
  // only while that organization is still the selected one (S3).
  const answeredOrganization = useRef<string | undefined>(undefined);

  const query = useQuery({
    queryKey: warehouseQueryKey(scope, organizationId, ...parts),
    // `enabled` does not stop a manual refetch in v4, hence the guard.
    queryFn: async ({ signal }) =>
      fetcher(requireOrganizationId(organizationId), signal),
    enabled: Boolean(organizationId) && (options.enabled ?? true),
    keepPreviousData:
      options.keepPreviousData === true &&
      answeredOrganization.current === organizationId,
    ...(options.retry !== undefined ? { retry: options.retry } : {}),
  });

  const ownAnswer = query.isSuccess && !query.isPreviousData;
  useEffect(() => {
    if (ownAnswer) {
      answeredOrganization.current = organizationId;
    }
  }, [ownAnswer, organizationId]);

  return query;
}
